import { z } from 'zod';
import { onboardingStepKeySchema, onboardingStepStatusSchema } from '../onboarding/steps.js';
import { isoDateStringSchema, objectIdSchema } from './common.js';

/**
 * What the setup checklist, the guided first order and the sample data look like on the wire.
 *
 * The shape is a REPORT, not a form: the client never tells the API how far along the shop is,
 * because most of the answer is a fact about the shop's own data — customers booked, staff
 * invited, WhatsApp connected — and a client-held checklist would drift the first time somebody
 * did one of those things from another screen or another phone.
 */

export const onboardingStepViewSchema = z.object({
  key: onboardingStepKeySchema,
  status: onboardingStepStatusSchema,
  completedAt: isoDateStringSchema.nullable(),
  autoCompleted: z.boolean(),
  available: z.boolean(),
});
export type OnboardingStepViewWire = z.infer<typeof onboardingStepViewSchema>;

/**
 * The activation moment worth optimising for: a first REAL order, booked with the shop's own
 * customer. Sample data does not count towards it, which is the whole reason the sample rows
 * are tracked by id.
 */
export const onboardingActivationSchema = z.object({
  activated: z.boolean(),
  firstOrderAt: isoDateStringSchema.nullable(),
  firstOrderId: objectIdSchema.nullable(),
  /** Hours from signup to that order — the number the product is actually judged on. */
  hoursToActivate: z.number().nullable(),
});
export type OnboardingActivation = z.infer<typeof onboardingActivationSchema>;

export const onboardingSampleDataSchema = z.object({
  loaded: z.boolean(),
  loadedAt: isoDateStringSchema.nullable(),
  customers: z.number().int().nonnegative(),
  profiles: z.number().int().nonnegative(),
  orders: z.number().int().nonnegative(),
});
export type OnboardingSampleData = z.infer<typeof onboardingSampleDataSchema>;

export const onboardingProgressSchema = z.object({
  steps: z.array(onboardingStepViewSchema),
  summary: z.object({
    total: z.number().int().nonnegative(),
    completed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    percentComplete: z.number().int().min(0).max(100),
    finished: z.boolean(),
  }),
  counts: z.object({
    customers: z.number().int().nonnegative(),
    staff: z.number().int().nonnegative(),
    orders: z.number().int().nonnegative(),
    measurementTemplates: z.number().int().nonnegative(),
    whatsappConnected: z.boolean(),
    gstAnswered: z.boolean(),
    shopDetailsFilled: z.boolean(),
  }),
  activation: onboardingActivationSchema,
  /** The guided first order: dismissed once, and never shown again. */
  tour: z.object({
    dismissed: z.boolean(),
    dismissedAt: isoDateStringSchema.nullable(),
    /** Which of the three guided actions the shop has already taken. */
    customerAdded: z.boolean(),
    measurementSaved: z.boolean(),
    orderBooked: z.boolean(),
  }),
  sample: onboardingSampleDataSchema,
});
export type OnboardingProgress = z.infer<typeof onboardingProgressSchema>;

/** A step being ticked off or waved away. Both are deliberate acts, so both are recorded. */
export const setOnboardingStepSchema = z.object({
  status: onboardingStepStatusSchema,
});
export type SetOnboardingStepInput = z.infer<typeof setOnboardingStepSchema>;
